import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Plus, Edit2, Trash2, X, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveHeadSetting = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [initializing, setInitializing] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, leaveType: null });
  const [addModal, setAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short_code: '',
    description: '',
    annual_quota: 12,
    is_paid: true,
    requires_document: false,
    color: '#3B82F6',
    is_active: true
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setInitializing(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/initialize-leave-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave types initialized successfully!' });
        fetchLeaveTypes();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to initialize leave types' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred' });
    } finally {
      setInitializing(false);
    }
  };

  const openEditModal = (leaveType) => {
    setFormData({
      name: leaveType.name,
      short_code: leaveType.short_code,
      description: leaveType.description || '',
      annual_quota: leaveType.annual_quota || 0,
      is_paid: leaveType.is_paid,
      requires_document: leaveType.requires_document || false,
      color: leaveType.color || '#3B82F6',
      is_active: leaveType.is_active
    });
    setEditModal({ show: true, leaveType });
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      short_code: '',
      description: '',
      annual_quota: 12,
      is_paid: true,
      requires_document: false,
      color: '#3B82F6',
      is_active: true
    });
    setAddModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types/${editModal.leaveType.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave type updated successfully!' });
        setEditModal({ show: false, leaveType: null });
        fetchLeaveTypes();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to update leave type' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while updating' });
    }
  };

  const handleAddNew = async () => {
    if (!formData.name || !formData.short_code) {
      setMessage({ type: 'error', text: 'Name and Short Code are required' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave type added successfully!' });
        setAddModal(false);
        fetchLeaveTypes();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to add leave type' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while adding' });
    }
  };

  const handleDelete = async (leaveType) => {
    if (!window.confirm(`Are you sure you want to delete "${leaveType.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types/${leaveType.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave type deleted successfully!' });
        fetchLeaveTypes();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to delete leave type' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while deleting' });
    }
  };

  const colorOptions = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ];

  // Form Modal Component
  const FormModal = ({ isEdit, onClose, onSave }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? 'Edit Leave Type' : 'Add New Leave Type'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Casual Leave"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Code *</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.short_code}
                onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })}
                placeholder="e.g., CL"
                maxLength={3}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this leave type"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Annual Quota (days)</label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.annual_quota}
                onChange={(e) => setFormData({ ...formData, annual_quota: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-gray-500 mt-1">Set 0 for unlimited</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <div className="flex gap-2 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${formData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_paid}
                onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Paid Leave</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_document}
                onChange={(e) => setFormData({ ...formData, requires_document: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Requires Doc</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Active</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {isEdit ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Type Settings</h1>
          <p className="text-gray-600 mt-1">Manage leave type configurations</p>
        </div>
        {leaveTypes.length > 0 && (
          <Button onClick={openAddModal} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Leave Type
          </Button>
        )}
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {leaveTypes.length === 0 && !loading && (
        <Card className="mb-6 bg-yellow-50 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">No Leave Types Configured</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Initialize standard leave types to get started with the leave management system.
                </p>
              </div>
            </div>
            <Button onClick={handleInitialize} disabled={initializing} className="gap-2">
              <Plus className="h-4 w-4" />
              {initializing ? 'Initializing...' : 'Initialize Standard Leave Types'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Configured Leave Types</span>
            <Badge variant="secondary">{leaveTypes.length} types</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : leaveTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No leave types configured yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {leaveTypes.map((type) => (
                <div
                  key={type.id}
                  className="border-2 rounded-lg p-5 hover:shadow-lg transition-shadow relative group"
                  style={{ borderColor: type.color }}
                >
                  {/* Edit/Delete Buttons */}
                  <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(type)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(type)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: type.color }}
                      >
                        {type.short_code}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{type.name}</h3>
                        <Badge variant={type.is_paid ? 'default' : 'secondary'} className="mt-1">
                          {type.is_paid ? '💰 Paid Leave' : '🚫 Unpaid Leave'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 min-h-[40px]">
                    {type.description}
                  </p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Annual Quota</div>
                      <div className="text-xl font-bold" style={{ color: type.color }}>
                        {type.annual_quota > 0 ? `${type.annual_quota} days` : 'Unlimited'}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Document Required</div>
                      <div className="text-xl font-bold" style={{ color: type.color }}>
                        {type.requires_document ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                    <div>Created by: {type.created_by}</div>
                    <Badge
                      variant="outline"
                      className={type.is_active ? 'border-green-500 text-green-700' : 'border-gray-400 text-gray-600'}
                    >
                      {type.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            About Leave Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-700">
            <div><strong>Casual Leave (CL):</strong> Short-term leave for personal work and emergencies.</div>
            <div><strong>Sick Leave (SL):</strong> Leave for medical reasons. Medical certificates may be required.</div>
            <div><strong>Earned Leave (EL):</strong> Also known as Privilege Leave, for planned vacations.</div>
            <div><strong>Unpaid Leave (UL):</strong> Leave without pay, deducted from salary.</div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editModal.show && (
        <FormModal
          isEdit={true}
          onClose={() => setEditModal({ show: false, leaveType: null })}
          onSave={handleSaveEdit}
        />
      )}

      {/* Add Modal */}
      {addModal && (
        <FormModal
          isEdit={false}
          onClose={() => setAddModal(false)}
          onSave={handleAddNew}
        />
      )}
    </div>
  );
};

export default LeaveHeadSetting;
